/**
 * The three rooms, as data.
 *
 * ============================== WHY A TABLE ====================================
 * `PRODUCT-FLOW-AND-PRICING.md` §3 is a map: a saloon with three doors, and behind each
 * door the SAME four tabs — Play · Train · Learn · Compete. If that shape lives in
 * three components it is three shapes, and the fourth room (or the fifth tab) is a
 * rewrite. So it lives here: the lobby draws its doors from this list, the shell draws
 * its tabs from it, and `App.tsx` builds its routes from it.
 *
 * The accent is a TOKEN NAME, never a hex. `art/tokens.css` owns the palette and
 * `tests/palette.test.ts` owns the tokens; a room that wanted a colour of its own would
 * have to argue for it there, which is exactly the argument that should be hard to win.
 * ===============================================================================
 */

/** The four tabs, in every room, in this order. Verb-named, per the master plan §4. */
export const TABS = [
  { id: "play", label: "Play" },
  { id: "train", label: "Train" },
  { id: "learn", label: "Learn" },
  { id: "compete", label: "Compete" },
] as const;

export type TabId = (typeof TABS)[number]["id"];

export interface Room {
  id: string;
  /** The route this room lives at. Never a separate tab; never a query parameter. */
  path: string;
  /** What the door and the room header say. */
  name: string;
  /** One line on the door. What the player gets, not what the engine does. */
  blurb: string;
  /** The CSS custom property the room paints its accent from. */
  accent: string;
  /**
   * The room's headline number, which is always in the header.
   *
   * `label` is what it is called; the VALUE is Phase 4's (net Bucks) and the trainer's
   * (Trainer Rating). Until those exist the header shows the label and a dash rather
   * than a number nobody has — the Phase 2 review's rule about the wallet gauge, which
   * holds for every number on the screen.
   */
  headline: string;
  /** `false` puts "Coming" on the door and in every tab. */
  open: boolean;
}

export const ROOMS: readonly Room[] = [
  {
    id: "poker",
    path: "/poker",
    name: "Poker",
    blurb: "Hold'em against the house cast. Tournaments, heads-up, and the trainer behind it.",
    accent: "--art-baize",
    headline: "Trainer Rating",
    open: true,
  },
  {
    id: "blackjack",
    path: "/blackjack",
    name: "Blackjack",
    blurb: "Two decks, reshuffled every hand, with the exact cost of every decision as you make it.",
    accent: "--art-oxblood",
    headline: "Accuracy",
    open: true,
  },
  {
    id: "cribbage",
    path: "/cribbage",
    name: "Cribbage",
    blurb: "The third room. Being built on the same kit.",
    accent: "--art-walnut",
    headline: "Rating",
    open: false,
  },
];

export function roomOf(id: string): Room | null {
  return ROOMS.find((r) => r.id === id) ?? null;
}
